/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@vsolv/core/logging/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { ThemeColorEnum } from '@vsolv/vectors-ui/theming';
import {
  BadgeColumn,
  ConnectedTablePage,
  IconButtonColumn,
  ProgressBarColumn,
  TableColumn,
  TextColumn,
  TextSubtitleColumn,
} from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { Policy, Warranty } from '@wsphere/warranties/domain';
import moment from 'moment';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  ArchivePolicyDialog,
  EditPolicyType,
  PolicyDialog,
  PublishPolicyDialog,
  RestorePolicyDialog,
} from '../../dialogs';
import { PolicyService } from '../../services';

export type PolicyTableFilters = Pick<Policy.ListPoliciesQueryRequest, 'search' | 'status'>;

@Component({ changeDetection: ChangeDetectionStrategy.Eager, templateUrl: './policies.page.html', standalone: false })
export class PoliciesPage implements OnInit {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private logger: LoggerService
  ) {}

  @ViewChild('page') private table?: ConnectedTablePage<Policy.Model>;

  @ViewChild('createPolicyDialog') createPolicyDialog?: PolicyDialog;
  @ViewChild('editPolicyDialog') editPolicyDialog?: PolicyDialog;
  @ViewChild('publishPolicyDialog') publishPolicyDialog!: PublishPolicyDialog;
  @ViewChild('archivePolicyDialog') archivePolicyDialog?: ArchivePolicyDialog;
  @ViewChild('restorePolicyDialog') restorePolicyDialog?: RestorePolicyDialog;

  @ViewChild('buttonRef') copyButton!: TemplateRef<ElementRef>;
  @ViewChild('draftTooltipRef') draftTooltipRef!: TemplateRef<ElementRef>;
  @ViewChild('publishedTooltipRef') publishedTooltipRef!: TemplateRef<ElementRef>;
  @ViewChild('archiveTooltipRef') archiveTooltipRef!: TemplateRef<ElementRef>;
  @ViewChild('runoffTooltipRef') runoffTooltipRef!: TemplateRef<ElementRef>;

  statuses = Object.keys(Policy.Status);

  selectedPolicy: Policy.Model | null = null;
  editPolicyType = EditPolicyType.POLICY_DETAILS;

  canCreate$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_Create', permissionKeys);
    })
  );

  canEdit$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_Edit', permissionKeys);
    })
  );

  canManageStatus$ = this.securitySvc.globalDistributors$.pipe(
    switchMap(globalDistributors => {
      const permissionKeys = globalDistributors?.map(dist => dist.permissionKey) ?? null;
      return this.securitySvc.hasAccess('pol_ManageStatus', permissionKeys);
    })
  );

  // Publish/archive/edit all report back through refreshPolicy(), which only feeds the policy detail
  // page. The table is fed by its own endpoint, so refetch it when a policy changes.
  protected refreshOnPolicyChange$ = this.policySvc.policyChanged.pipe(tap(() => this.table?.datasource?.refresh()));

  filters: PolicyTableFilters | null = null;
  protected filters$ = new BehaviorSubject(this.filters);
  // This table only renders scalars, so it asks for the light response. Other callers of /api/policies
  // send nothing and keep the full payload.
  protected queryParams$ = this.filters$.pipe(
    distinctUntilChanged((p, n) => JSON.stringify(p) === JSON.stringify(n)),
    map(filters => ({ ...(filters ?? {}), lean: true }))
  );

  columns$: Observable<TableColumn<unknown>[]> = combineLatest([this.canEdit$, this.canManageStatus$]).pipe(
    distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
    map(([canEdit, canManageStatus]) => {
      return [
        new TextSubtitleColumn<unknown>({ header: 'Title', sortable: true }, (item: any) => ({
          title: item.title,
          subtitle: item.policyNumber,

          tooltipStyle: 'light',
          tooltipCloseDelay: 1000,
          tooltipTemplate: this.copyButton,
        })),

        new BadgeColumn({ header: 'Status', sortable: true }, (item: any) => ({
          text: item.status.charAt(0) + item.status.slice(1).toLowerCase(),
          displayStatusIcon: true,
          tooltipStyle: 'light',
          tooltipTemplate:
            item.status === Policy.Status.DRAFT
              ? this.draftTooltipRef
              : item.status === Policy.Status.PUBLISHED
                ? undefined
                : item.status === Policy.Status.ARCHIVED
                  ? this.archiveTooltipRef
                  : item.status === Policy.Status.RUNOFF
                    ? this.runoffTooltipRef
                    : undefined,
          theme:
            item.status === Policy.Status.DRAFT
              ? 'info'
              : item.status === Policy.Status.PUBLISHED
                ? 'success'
                : item.status === Policy.Status.ARCHIVED
                  ? 'default'
                  : item.status === Policy.Status.RUNOFF
                    ? 'warn'
                    : 'default',
        })),

        new TextColumn({ header: 'Date created', sortable: true }, (item: any) => ({
          text: moment(item.created as Date).format('MMM DD, YYYY'),
        })),

        // Counts come down with the row; cells are rebuilt often, so a fetch here refired every time.
        new ProgressBarColumn({ header: 'No. of warranties' }, (item: any) => {
          const warrantyCounts = (item.warrantyCounts ?? null) as Record<string, number> | null;
          if (!warrantyCounts) return { endText: '', config: { items: [] } };

          const total = Object.values(warrantyCounts).reduce((acc, count) => acc + count, 0);
          if (!total) return { endText: '', config: { items: [] } };

          return {
            startText: this.largeNumberFormat(total),
            config: {
              items: [
                { theme: ThemeColorEnum.default, weight: warrantyCounts[Warranty.Status.DRAFT] },
                {
                  theme: ThemeColorEnum.success,
                  weight: warrantyCounts[Warranty.Status.ACTIVATED] + warrantyCounts[Warranty.Status.REGISTERED],
                },
                { theme: ThemeColorEnum.warn, weight: warrantyCounts[Warranty.Status.EXPIRED] },
                { theme: ThemeColorEnum.danger, weight: warrantyCounts[Warranty.Status.CANCELLED] },
              ],
            },
          };
        }),
        ...(canManageStatus
          ? [
              new IconButtonColumn({ stickyEnd: true, fitContent: true }, (item: any) => ({
                type: 'clear',
                rounded: true,
                icon:
                  item.status === Policy.Status.DRAFT
                    ? 'file-check-02'
                    : item.status === Policy.Status.PUBLISHED
                      ? 'archive'
                      : item.status === Policy.Status.ARCHIVED || item.status === Policy.Status.RUNOFF
                        ? 'refresh-ccw-01'
                        : 'delete',
                tooltipText:
                  item.status === Policy.Status.DRAFT
                    ? 'Publish'
                    : item.status === Policy.Status.PUBLISHED
                      ? 'Archive'
                      : item.status === Policy.Status.ARCHIVED || item.status === Policy.Status.RUNOFF
                        ? 'Restore'
                        : undefined,
                click: () => {
                  item.status === Policy.Status.DRAFT
                    ? this.openPublishPolicyDialog(item)
                    : item.status === Policy.Status.PUBLISHED
                      ? this.openArchivePolicyDialog(item)
                      : this.openRestorePolicyDialog(item);
                },
              })),
            ]
          : []),
        ...(canEdit
          ? [
              new IconButtonColumn({ stickyEnd: true, fitContent: true }, (item: any) => ({
                type: 'clear',
                rounded: true,
                icon: 'edit-01',
                tooltipText: 'Edit',
                click: () => this.openEditPolicyDialog(item),
              })),
            ]
          : []),
      ];
    }),
    shareReplay(1)
  );

  async refreshTable(filters?: { search?: string; status?: Policy.Status }) {
    if (filters?.search !== undefined) {
      this.logger.debug('Refreshing policies table with search filter', { search: filters.search });
      this.filters = { ...this.filters, search: filters.search };
    }
    if (filters && 'status' in filters) {
      this.logger.debug('Refreshing policies table with status filter', { status: filters.status });
      if (filters.status) {
        this.filters = { ...(this.filters ?? {}), status: filters.status };
      } else if (this.filters?.status) {
        const nextFilters = { ...this.filters };
        delete nextFilters.status;
        this.filters = Object.keys(nextFilters).length ? nextFilters : null;
      }
    }
    this.filters$.next(this.filters);
    this.syncFiltersToUrl();
  }

  onStatusChange(status: Policy.Status | readonly Policy.Status[] | null) {
    const resolvedStatus = Array.isArray(status) ? (status[0] ?? null) : status;
    const selectedStatus = this.filters?.status ?? null;
    const nextStatus = selectedStatus === resolvedStatus ? null : resolvedStatus;
    this.refreshTable({ status: nextStatus ?? undefined });
  }

  private syncFiltersToUrl() {
    const search = this.filters?.search || null;
    const status = this.filters?.status ?? null;
    const params = this.route.snapshot.queryParamMap;

    if ((params.get('search') || null) === search && (params.get('status') || null) === status) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { search, status },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private getFiltersFromUrl(): { search?: string; status?: Policy.Status } {
    const params = this.route.snapshot.queryParamMap;
    const search = params.get('search') || undefined;
    const statusParam = params.get('status');
    const status =
      statusParam && (Object.values(Policy.Status) as string[]).includes(statusParam)
        ? (statusParam as Policy.Status)
        : undefined;

    return {
      ...(search !== undefined ? { search } : {}),
      ...(status !== undefined ? { status } : {}),
    };
  }

  async downloadPolicies() {
    this.logger.info('Downloading policies CSV');
    const response = await this.policySvc.export().catch(({ error }) => {
      this.logger.error('Failed to download policies', error);
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });
    });

    if (response) {
      this.logger.info('Policies CSV downloaded successfully');
      const link = document.createElement('a');
      link.setAttribute('href', response.data);
      link.setAttribute('download', 'Policies.csv');
      link.click();
    }
  }

  // Edit and publish read plans, coverages, emails, terms and documents, which lean rows don't carry.
  // Archive and restore only need id/title/status, so they use the row as-is.
  async openEditPolicyDialog(policy: Policy.Model) {
    this.logger.info('Opening edit policy dialog', { policyId: policy.id, policyTitle: policy.title });
    this.selectedPolicy = (await this.policySvc.getOne(policy.id).catch(() => null)) ?? policy;
    this.editPolicyDialog?.open();
  }

  async openPublishPolicyDialog(policy: Policy.Model) {
    this.logger.info('Opening publish policy dialog', { policyId: policy.id, policyTitle: policy.title });
    this.selectedPolicy = (await this.policySvc.getOne(policy.id).catch(() => null)) ?? policy;
    this.publishPolicyDialog.open();
  }

  openArchivePolicyDialog(policy: Policy.Model) {
    this.logger.info('Opening archive policy dialog', { policyId: policy.id, policyTitle: policy.title });
    this.selectedPolicy = policy;
    this.archivePolicyDialog?.open();
  }

  openRestorePolicyDialog(policy: Policy.Model) {
    this.logger.info('Opening restore policy dialog', { policyId: policy.id, policyTitle: policy.title });
    this.selectedPolicy = policy;
    this.restorePolicyDialog?.open();
  }

  navigateTo(policyId?: string) {
    if (policyId) this.router.navigate([`${policyId}`], { relativeTo: this.route });
  }

  copyPolicyNumber(policyNum: string) {
    this.logger.debug('Policy number copied to clipboard', { policyNumber: policyNum });
    this.toastSvc.show({
      type: 'success',
      title: 'Policy number copied',
      text:
        'Policy number <strong>' + policyNum + '</strong> was successfully copied to clipboard and can now be shared.',
    });
  }

  async ngOnInit() {
    this.logger.info('Policies page initialized');
    this.logger.debug('Loading policies table');
    const urlFilters = this.getFiltersFromUrl();
    await this.refreshTable(Object.keys(urlFilters).length ? urlFilters : undefined);
  }

  private largeNumberFormat(value: number) {
    return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: value >= 1000 ? 2 : 0 }).format(
      value
    );
  }
}
